import ErrorProvider from './context/ErrorContext'
import AuthProvider from './context/AuthContext'
import React from 'react'
import App from './App'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { ModalProvider } from './context/ModalContext'
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as Sentry from '@sentry/react'
import ErrorPage from './pages/ErrorPage'
import { setupSentry } from './shared/sentry'
import { logError } from './shared/logger'
import { setupCrisp } from './shared/crisp'
import { Elements } from '@stripe/react-stripe-js'
import { setupStripe, stripeElementsOptions } from './shared/stripe'
import { setupPostHog } from './shared/posthog'
import { consumeSelectedOrganizationId } from './shared/api'
import { Toaster } from 'sonner'
import './index.css'

setupSentry()
setupCrisp()
setupPostHog()
consumeSelectedOrganizationId()

const stripe = setupStripe()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Server errors and rate limiting are transient; back off and retry
      // instead of surfacing a broken page.
      retry: (failureCount: number, error: any) => {
        if (failureCount >= 3) return false
        return error?.status >= 500 || error?.status === 429
      },
      retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 30000)
    }
  },

  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query?.state?.data !== undefined) {
        logError(error)
      }
    }
  })
})

const container = document.getElementById('root')!
const root = createRoot(container)

root.render(
  <React.StrictMode>
    {/* Without a boundary, any uncaught render error unmounts the whole tree
        and leaves a blank white page. */}
    <Sentry.ErrorBoundary
      fallback={
        <ErrorPage
          message="Something went wrong"
          action={() => window.location.reload()}
          actionTitle="Reload"
        />
      }
    >
      <BrowserRouter>
        <Elements stripe={stripe} options={stripeElementsOptions}>
          <QueryClientProvider client={queryClient}>
            <ErrorProvider>
              <AuthProvider>
                <ModalProvider>
                  <Toaster richColors />
                  <App />
                </ModalProvider>
              </AuthProvider>
            </ErrorProvider>
          </QueryClientProvider>
        </Elements>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
)
